import { Check, Circle, X } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export interface PasswordRule {
  id: string;
  /** Label shown next to the indicator. */
  label: React.ReactNode;
  /** Predicate evaluated on every render. */
  test: (value: string) => boolean;
}

export type PasswordRequirementsTone = "default" | "strict";

export interface PasswordRequirementsProps
  extends Omit<React.HTMLAttributes<HTMLUListElement>, "title"> {
  /** Current password value. */
  value: string;
  /** Optional override of the rules list. */
  rules?: PasswordRule[];
  /**
   * `default` shows pending rules as muted circles.
   * `strict` shows pending rules as red X icons — best after the user has
   * tried to submit.
   */
  tone?: PasswordRequirementsTone;
  /** Heading shown above the list (default "Your password must include"). */
  title?: React.ReactNode;
  /** Hide the heading entirely. */
  hideTitle?: boolean;
}

export const DEFAULT_PASSWORD_RULES: PasswordRule[] = [
  {
    id: "length",
    label: "At least 8 characters",
    test: (v) => v.length >= 8,
  },
  {
    id: "uppercase",
    label: "At least one uppercase letter",
    test: (v) => /[A-Z]/.test(v),
  },
  {
    id: "lowercase",
    label: "At least one lowercase letter",
    test: (v) => /[a-z]/.test(v),
  },
  {
    id: "number",
    label: "At least one number",
    test: (v) => /\d/.test(v),
  },
  {
    id: "symbol",
    label: "At least one symbol (! @ # $ …)",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

/**
 * PasswordRequirements — live validation checklist for password fields.
 *
 * Each rule renders with a check / pending / fail icon based on whether the
 * current value satisfies it. The rules list is fully data-driven so apps
 * can match their own backend policy without forking the component.
 *
 * Default rules cover the common minimum (length, uppercase, lowercase,
 * number, symbol). Override `rules` to add / remove / reorder.
 *
 * Dumb component: the parent passes the current value as a prop. Internally
 * we just evaluate each rule's predicate.
 */
export const PasswordRequirements = React.forwardRef<HTMLUListElement, PasswordRequirementsProps>(
  function PasswordRequirements(
    {
      className,
      hideTitle,
      rules = DEFAULT_PASSWORD_RULES,
      title = "Your password must include",
      tone = "default",
      value,
      ...props
    },
    ref,
  ) {
    return (
      <div className="cw-v2-password-requirements__wrap">
        {hideTitle ? null : <div className="cw-v2-password-requirements__title">{title}</div>}
        <ul
          ref={ref}
          className={cx(
            "cw-v2-password-requirements",
            `cw-v2-password-requirements--${tone}`,
            className,
          )}
          {...props}
        >
          {rules.map((rule) => {
            const passes = rule.test(value);
            const state = passes ? "pass" : tone === "strict" ? "fail" : "pending";
            const Icon = state === "pass" ? Check : state === "fail" ? X : Circle;
            return (
              <li
                key={rule.id}
                className={cx(
                  "cw-v2-password-requirements__item",
                  `cw-v2-password-requirements__item--${state}`,
                )}
              >
                <Icon className="cw-v2-password-requirements__icon" aria-hidden />
                <span className="cw-v2-password-requirements__label">{rule.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  },
);
PasswordRequirements.displayName = "PasswordRequirements";

/** Convenience helper — true when every rule passes against the value. */
export function isPasswordValid(
  value: string,
  rules: PasswordRule[] = DEFAULT_PASSWORD_RULES,
): boolean {
  return rules.every((rule) => rule.test(value));
}
